import i18next from 'i18next'

import en from './translations/en.json'
import fr from './translations/fr.json'
import es from './translations/es.json'
import pt from './translations/pt.json'
import it from './translations/it.json'
import de from './translations/de.json'
import nl from './translations/nl.json'
import ru from './translations/ru.json'
import jp from './translations/jp.json'
import cn from './translations/cn.json'
import ar from './translations/ar.json'
import he from './translations/he.json'

/** Same language set as admin venue / menu translation locales (short codes). */
const SUPPORTED_LOCALES = ['fr', 'en', 'es', 'pt', 'de', 'it', 'nl', 'ru', 'jp', 'cn', 'ar', 'he']

const RTL_LOCALES = new Set(['ar', 'he'])

/** Map flag / country / navigator codes to i18n resource keys. */
const LANGUAGE_ALIASES = {
  fr: 'fr',
  en: 'en',
  es: 'es',
  pt: 'pt',
  de: 'de',
  it: 'it',
  nl: 'nl',
  ru: 'ru',
  jp: 'jp',
  ja: 'jp',
  cn: 'cn',
  zh: 'cn',
  ar: 'ar',
  he: 'he',
  // English flag / region codes stored by the web app
  gb: 'en',
  uk: 'en',
  us: 'en',
  // Hebrew is stored as IL (from he-IL / he_IL)
  il: 'he',
}

/**
 * Normalize navigator / flag / locale codes to a supported i18n language.
 * Examples: "FR" → "fr", "en-GB" → "en", "IL" → "he", "jp-JP" → "jp", "zh-CN" → "cn"
 */
const normalizeLanguage = (lng) => {
  if (!lng || lng === 'undefined') {
    return 'fr'
  }
  const parts = String(lng).toLowerCase().split(/[-_]/)
  for (const part of parts) {
    if (LANGUAGE_ALIASES[part]) {
      return LANGUAGE_ALIASES[part]
    }
    if (SUPPORTED_LOCALES.includes(part)) {
      return part
    }
  }
  return 'fr'
}

const isRtlLanguage = (lng) => RTL_LOCALES.has(normalizeLanguage(lng))

/**
 * Flag / country codes (from venue.supportedLanguages) → API locale used by admin.
 * Matches packages/admin venueLocales ids.
 */
const FLAG_TO_API_LOCALE = {
  FR: 'fr-FR',
  EN: 'en-GB',
  GB: 'en-GB',
  UK: 'en-GB',
  US: 'en-GB',
  ES: 'es-ES',
  PT: 'pt-PT',
  IT: 'it-IT',
  DE: 'de-DE',
  NL: 'nl-NL',
  RU: 'ru-RU',
  JP: 'jp-JP',
  CN: 'cn-CN',
  AR: 'ar-AR',
  IL: 'he-IL',
}

/** Convert a flag code or locale string to the API locale (e.g. IL → he-IL). */
const toApiLocale = (flagOrLocale) => {
  if (!flagOrLocale || flagOrLocale === 'undefined') {
    return 'fr-FR'
  }
  const raw = String(flagOrLocale).trim()
  if (raw.includes('-') || raw.includes('_')) {
    const normalized = raw.replace('_', '-')
    if (/^(he|il)[-_]/i.test(normalized)) {
      return 'he-IL'
    }
    if (/^(ja|jp)[-_]/i.test(normalized)) {
      return 'jp-JP'
    }
    if (/^(zh|cn)[-_]/i.test(normalized)) {
      return 'cn-CN'
    }
    return normalized
  }
  const upper = raw.toUpperCase()
  return FLAG_TO_API_LOCALE[upper] || `${raw.toLowerCase()}-${upper}`
}

const initialLanguage = normalizeLanguage(
  typeof localStorage !== 'undefined'
    ? localStorage.getItem('language') || (typeof navigator !== 'undefined' ? navigator.language : 'fr')
    : 'fr'
)

i18next.init({
  lng: initialLanguage,
  fallbackLng: 'fr',
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    es: { translation: es },
    pt: { translation: pt },
    it: { translation: it },
    de: { translation: de },
    nl: { translation: nl },
    ru: { translation: ru },
    jp: { translation: jp },
    cn: { translation: cn },
    ar: { translation: ar },
    he: { translation: he },
  },
})

const t = (...args) => i18next.t(...args)

const changeLanguage = (lng, callback) => {
  const normalized = normalizeLanguage(lng)
  if (typeof document !== 'undefined') {
    document.documentElement.dir = isRtlLanguage(normalized) ? 'rtl' : 'ltr'
  }
  return i18next.changeLanguage(normalized, callback)
}

export {
  i18next,
  t,
  changeLanguage,
  normalizeLanguage,
  isRtlLanguage,
  toApiLocale,
  SUPPORTED_LOCALES,
  RTL_LOCALES,
}
